import { Component, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { findIndex, isEmpty, isEqual, differenceWith, debounce, isNil } from 'lodash-es';
import { SeButtonsToolbarService } from './se-buttons-toolbar.service';
import { RootState } from '../../root-store/root.state';
import {
    seToolbarButtonPressed,
    seToolbarButtonToggle,
    seToolbarSetRightSlider,
    seToolbarSetLeftSlider,
    seToolbarSetTimeline,
} from './redux/se-buttons-toolbar.actions';
import { ToolbarAction, ToolbarButtonData, ToolbarSliderOptions, ToolbarTimelineOptions } from './types';
import { SLIDER_OPTIONS, TIMELINE_OPTIONS } from './redux/se-buttons-toolbar.state';

@Component({
    selector: 'se-buttons-toolbar',
    templateUrl: './se-buttons-toolbar.component.html',
    styleUrls: ['./se-buttons-toolbar.component.scss'],
})
export class SeButtonsToolbarComponent {
    @ViewChild('headerButtonsContainer')
    headerButtonsContainer : ElementRef;

    @HostListener('window:resize')
    onResize() {
        this.refreshButtonsVisibility();
    }

    private _buttonsData: ToolbarButtonData[] = [];
    @Input()
    set buttonsData(value: ToolbarButtonData[] | null) {

        this._buttonsData = value || [];

        this.refreshButtonsVisibility();
    }
    get buttonsData(): ToolbarButtonData[] {
        return this._buttonsData;
    }

    @Input()
    disableAll = false;

    @Input()
    backVisible = false;

    @Input()
    rightSliderOptions: ToolbarSliderOptions | null;

    @Input()
    leftSliderOptions : ToolbarSliderOptions | null;

    @Input()
    timelineOptions? : ToolbarTimelineOptions | null;

    private _background = undefined;
    @Input()
    set background(value) {
        if (this._background != value) {
            this._background = value;
        }
    }
    get background() {
        return this._background;
    }

    set rightSliderChecked(value: boolean) {
        const options = this.rightSliderOptions ?? SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED;
        this.store.dispatch(
            seToolbarSetRightSlider({
                status: { ...options, checked: value },
            }),
        );
    }

    get rightSliderChecked() {
        return this.rightSliderOptions && this.rightSliderOptions.checked;
    }

    set leftSliderChecked(value: boolean) {
        const options = this.leftSliderOptions ?? SLIDER_OPTIONS.LEFT_SLIDER_DISABLED;
        this.store.dispatch(
            seToolbarSetLeftSlider({
                status: { ...options, checked: value },
            })
        );
    }
    get leftSliderChecked() {
        return this.leftSliderOptions && this.leftSliderOptions.checked;
    }

    set timelineVisible (value : boolean) {
        const options = this.timelineOptions ?? TIMELINE_OPTIONS.TIMELINE_DISABLED;
        this.store.dispatch(
            seToolbarSetTimeline({
                status: { ...options, open: value },
            }),
        );
    }

    get timelineVisible () {
        return this.timelineOptions && this.timelineOptions.open; // TODO_DGS implementare su WL
    }

    get getLeftTooltip():string {
        if (this.leftSliderChecked) {
            return this.translate.instant('CloseLeftPanel');
        }

        return this.translate.instant('OpenLeftPanel');
    }

    get getRightTooltip(): string {
        if (this.rightSliderChecked) {
            return this.translate.instant('OpenRightPanel');
        }

        return this.translate.instant('CloseRightPanel');
    }

    buttonsList: Readonly<ToolbarButtonData>[] = [];
    hideButtonsList: Readonly<ToolbarButtonData>[] = [];

    buttonsContainerWidth: number;

    prevLabel = this.translate.instant('PreviousPage');

    constructor(private store: Store<RootState>, private translate: TranslateService) { }

    isButtonsDataDifferent(value: ToolbarButtonData[]) {

        const array = differenceWith(value, this._buttonsData, (a, b) => {
            return isEqual(a, b);
        });

        return !!array && array.length > 0;
    }

    isMoreButtonVisible(): boolean {
        return !isEmpty(this.hideButtonsList);
    }

    onToolbarClickEvent(button: ToolbarButtonData) {
        if (button.isToggle) {
            // Se è un toggle carico a mano il css
            const checked = SeButtonsToolbarService.toggleToolbarButton(
                button.actionType,
                this.buttonsList,
                this.hideButtonsList
            );
            this.store.dispatch(seToolbarButtonToggle({ button: button.actionType, checked }));
        } else {
            this.store.dispatch(seToolbarButtonPressed({ button: button.actionType, payload: button.payload, idCardioWebServiceForExtUrlToken: button.idCardioWebServiceForExtUrlToken }));
        }
    }

    onLeftSliderChange(e: MatSlideToggleChange) {
        this.leftSliderChecked = e.checked;
    }

    _onTimelineToggle (event: boolean) {
        this.timelineVisible = event;
    }

    onBackClick() {
        this.store.dispatch(seToolbarButtonPressed({ button: ToolbarAction.BACK }));
    }

    getButtonAction(_: number, button: ToolbarButtonData) {
        return button.actionType;
    }

    getVisibilityToolbar(button: ToolbarButtonData): boolean {
        if (!isNil(button.status.visibleIntoToolbar) && !button.hasChildren) {
            if  (button.status.visible) {
                return button.status.visibleIntoToolbar;
            }
        } else {
            return (button.status.visible && !button.hasChildren);
        }
    }

    getVisibilityChildrenToolbar (button : ToolbarButtonData) : boolean {
        if (!isNil(button.status.visibleIntoToolbar) && button.hasChildren) {
            if (button.status.visible) {
                return button.status.visibleIntoToolbar;
            }
        } else {
            return (button.status.visible && button.hasChildren);
        }
    }

    private refreshButtonsVisibility = debounce(() => {
        this.hideButtonsList = [];
        setTimeout(() => {
            // Copio la collection passata come parametro nella collection interna
            this.buttonsList = this.buttonsData.slice();

            setTimeout(() => {
                this.setupInitialButtonsVisibility();
            });
        });
    }, 100);

    // Imposta i pulsanti visibili e quelli non visibili al caricamento
    private setupInitialButtonsVisibility () : any {
        this.buttonsContainerWidth = this.headerButtonsContainer.nativeElement.clientWidth; // Larghezza Originale

        const domButtonlist = this.headerButtonsContainer.nativeElement.querySelectorAll('button') as HTMLCollection;
        let totalWidth = 0;

        if (isEmpty(domButtonlist)) {
            return;
        }

        for (let i = 0; i < domButtonlist.length; i++) {
            const domElement = domButtonlist[i];
            totalWidth += domElement.clientWidth;
            if (totalWidth > this.buttonsContainerWidth) {
                let length = domButtonlist.length; // non uso : booleanthis.buttonsList perchè quella include anche quelli non visibili

                // Pulisco la lista dei nascoti che andro' a ricostruire...
                //if (this.hideButtonsList && this.hideButtonsList.length > 0)
                //    this.hideButtonsList.splice(0, this.hideButtonsList.length);

                for (let n = i; n < length; n++) {
                    // A 'i' ho superato la lunghezza quindi parto a nascondere dall' 'i-1 esimo'
                    // Estraggo il pulsante dal DOM (mi serve per l'id)
                    const domBtn = domButtonlist[n];

                    //lavoro sulla collection in binding e rimuovo l'item che corrisponde all'id selezionato
                    const btn = this.buttonsList.splice(
                        findIndex(this.buttonsList, item => item.actionType === domBtn.getAttribute('action-type')),
                        1
                    );
                    this.hideButtonsList.push(btn[0]);
                }
                break;
            }
        }
    }
}
